import React, { useState, useEffect, useCallback } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Row, Col, Button, Form as BSForm } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import {
  FormikInput,
  UploadImage,
  FormikRangePicker,
  DocumentEditor,
  UploadFile,
  FormikSelect,
  ButtonTooltip,
  Icon,
  ComponentDataTable,
  DatatableEmployeeName,
  SimpleCenteredModal,
  EmployeeSearchModal
} from '../../components';
import '../../services/yupCustomMethods';
import { indexEmployeesRequest } from '../../requests/employees';

const selectorTypes = [{ label: 'Todos', value: 'all_employees' }];

const CompanyAnnouncementForm = ({
  announcement = {},
  errors,
  touched,
  values,
  setFieldValue,
  setFieldTouched,
  isSubmitting
}) => {
  const { startDate, endDate } = values.announcement;

  const dispatch = useDispatch();
  const [selector, setSelector] = useState(selectorTypes);
  const [filterEmployees, setFilterEmployees] = useState([]);
  const [onRequest, setOnRequest] = useState(false);
  const [modalShow, setModalShow] = useState(false);

  const removeFromTable = id => {
    setFilterEmployees(prev => prev.filter(e => e.id !== id));
    setFieldValue(
      'announcement[employeeIds]',
      values.announcement.employeeIds.filter(v => v !== id)
    );
    const removed = filterEmployees.find(e => e.id === id);
    if (removed) setSelector(prev => [...prev, removed]);
  };

  const handleSearch = (selectedEmployees = []) => {
    const vSelector = selector.filter(opt => !selectedEmployees.includes(opt.value));
    setModalShow(false);
    if (vSelector.length === selector.length) return;

    const employeesToAdd = selector.filter(opt => selectedEmployees.includes(opt.value));
    const currentIdsStr = values.announcement.employeeIds.map(String);
    const idsToAdd = employeesToAdd.map(it => it.value).filter(id => !currentIdsStr.includes(String(id)));

    setFieldValue('announcement[employeeIds]', [...values.announcement.employeeIds, ...idsToAdd]);

    setFilterEmployees(prev => {
      const existing = new Set(prev.map(e => String(e.id)));
      const newOnes = employeesToAdd.filter(e => !existing.has(String(e.id)));
      return [...prev, ...newOnes];
    });

    setSelector(vSelector);
  };

  const columns = [
    {
      name: 'RUT Y NOMBRE',
      selector: 'name',
      cell: item => <DatatableEmployeeName item={item} />,
      grow: '2',
      sortable: true
    },
    {
      name: 'ACCIONES',
      cell: row => (
        <ButtonTooltip onClick={() => removeFromTable(row.id)} variant="circle-danger" size="sm" text="Eliminar">
          <Icon icon="trash" />
        </ButtonTooltip>
      ),
      grow: 1,
      right: true
    }
  ];

  const fetchEmployees = useCallback(
    currentEmployeeIds => {
      setOnRequest(true);
      indexEmployeesRequest({
        dispatch,
        params: { is_dt: false, paginate: false },
        successCallback: res => {
          const employees = res.data.data.map(e => ({
            label: e.full_name,
            value: e.id,
            ...e
          }));

          const selectedIds = currentEmployeeIds.map(String);
          const preSelected = employees.filter(e => selectedIds.includes(String(e.value)));
          const available = employees.filter(e => !selectedIds.includes(String(e.value)));

          setFilterEmployees(preSelected);
          setSelector([...selectorTypes, ...available]);
          setOnRequest(false);
        },
        failureCallback: () => setOnRequest(false)
      });
    },
    [dispatch]
  );

  const addAllEmployees = () => {
    const ids = selector.filter(e => e.id).map(e => e.value);
    const items = selector.filter(e => e.id);
    setFilterEmployees(prev => [...prev, ...items]);
    setFieldValue('announcement[employeeIds]', [...ids, ...values.announcement.employeeIds]);
    setSelector(selectorTypes);
  };

  const handleSelector = () => {
    if (values.employeeSelector === 'all_employees') {
      addAllEmployees();
      return;
    }

    const toAdd = selector.find(e => e.value === values.employeeSelector);
    if (!toAdd) {
      return;
    }

    setFilterEmployees(prev => [...prev, toAdd]);
    setFieldValue('announcement[employeeIds]', [...values.announcement.employeeIds, toAdd.value]);
    setSelector(prev => prev.filter(e => e.value !== toAdd.value));
    setFieldValue('employeeSelector', '');
  };

  useEffect(() => {
    fetchEmployees(values.announcement.employeeIds);
  }, [fetchEmployees, values.announcement.employeeIds]);

  return (
    <>
      <h2 className="mt-5 mb-3">Anuncios</h2>
      <div className="yellow-wrapper">
        <Form id="announcement-form" className="form-announcement">
          <Row>
            <Col md={6}>
              <Row>
                <Col>
                  <Field name="announcement[name]">
                    {({ field }) => (
                      <FormikInput
                        {...field}
                        abbr
                        label="Nombre del anuncio"
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
                <Col>
                  <Field name="announcement[position]">
                    {({ field }) => (
                      <FormikInput
                        {...field}
                        abbr
                        label="Posición"
                        inputType="number"
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
              </Row>

              <Field name="rangeDate">
                {({ field }) => (
                  <FormikRangePicker
                    {...field}
                    abbr
                    startDateName="announcement[startDate]"
                    endDateName="announcement[endDate]"
                    labelLeft="Publicar Desde"
                    startDate={startDate}
                    endDate={endDate}
                    minCurrentDate
                    showClearDates
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>

              <Field name="announcement[link]">
                {({ field }) => (
                  <FormikInput
                    {...field}
                    label="Link"
                    placeholder="https://peoplework.cl"
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>

              <Field name="announcement[image]">
                {({ field }) => (
                  <UploadImage
                    {...field}
                    name="Agregar imagen"
                    imageUrl={getIn(announcement.image, 'fileUrl')}
                    onChange={image => setFieldValue(field.name, image)}
                    helpText="Formatos compatibles: jpg, png, jpeg, gif. Tamaño máximo 3 MB."
                    fileAccept=".png, .jpg, .gif, .jpeg"
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>

              <Field name="announcement[attachedFile]">
                {({ field }) => (
                  <UploadFile
                    {...field}
                    name="Archivo"
                    label="Documento adjunto"
                    helpText="Formatos: jpg, png, jpeg, gif, doc, docx, pdf. Máx 3 MB."
                    onChange={file => setFieldValue(field.name, file)}
                    fileAccept=".png, .jpg, .jpeg, .gif, .doc, .docx, .pdf"
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>

              <Field name="announcement[notifyByEmail]">
                {({ field }) => (
                  <BSForm.Check
                    {...field}
                    type="checkbox"
                    label="Notificación por correo"
                    checked={field.value}
                    onChange={e => setFieldValue(field.name, e.target.checked)}
                  />
                )}
              </Field>
            </Col>

            {/* Columna derecha */}
            <Col md={6}>
              <Field name="announcement[description]">
                {({ field }) => (
                  <DocumentEditor
                    abbr
                    label="Descripción"
                    data={values.announcement.description}
                    onEditorChange={content => setFieldValue(field.name, content)}
                    onBlur={() => setFieldTouched(field.name, true)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="info-box ml-0 mt-4">
        <h4 className="text-uppercase">Selecciona Trabajadores</h4>
        <p className="info">
          <span className="full-width">Seleccione trabajadores a los que se asignará este anuncio</span>
        </p>
      </div>

      <Row className="align-items-center">
        <Col md={5}>
          <Field name="employeeSelector">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Asignar a"
                placeholder="Seleccionar trabajador"
                options={selector.sort((a, b) => a.label?.localeCompare(b.label))}
                onChange={opt => setFieldValue(field.name, opt ? opt.value : '')}
              />
            )}
          </Field>
        </Col>
        <Col className="ml-2">
          <ButtonTooltip
            variant="circle-primary"
            className="advance-search"
            toolbarVariant="mt-n3"
            text="Búsqueda avanzada de empleados mediante filtros de cargos, Lugares de Prestación de Servicios, otros"
            onClick={() => setModalShow(true)}
          >
            <Icon className="w-100 h-100" icon="people-circle" />
          </ButtonTooltip>
        </Col>

        <Col xs={6} md={2}>
          <Button variant="primary" onClick={handleSelector} style={{ marginTop: 5 }}>
            Agregar
          </Button>
        </Col>
      </Row>

      <ComponentDataTable
        columns={columns}
        data={filterEmployees}
        onRequest={onRequest}
        selectableRows={false}
        pagination
      />

      <SimpleCenteredModal
        title="Buscar Empleados"
        body={
          <EmployeeSearchModal
            customParams={{ active: true }}
            handleClose={() => setModalShow(false)}
            formRequest={handleSearch}
          />
        }
        show={modalShow}
        onHide={() => setModalShow(false)}
      />

      <Row className="mt-4">
        <Col className="d-flex justify-content-end">
          <Button type="submit" form="announcement-form" disabled={isSubmitting} variant="primary">
            GUARDAR
          </Button>
        </Col>
      </Row>
    </>
  );
};

const setInitialValues = props => {
  const announcement = props.announcement || {};
  const {
    image,
    description,
    endDate,
    employeeIds,
    link,
    name,
    position,
    startDate,
    notifyByEmail,
    attachedFile
  } = announcement;

  return {
    announcement: {
      image,
      description,
      endDate,
      employeeIds: employeeIds || [],
      link,
      name,
      position,
      startDate,
      attachedFile,
      notifyByEmail: notifyByEmail || false
    },
    employeeSelector: 'all_employees',
    rangeDate: [startDate, endDate]
  };
};

const validationSchema = Yup.object().shape({
  announcement: Yup.object().shape({
    description: Yup.string().required('Debes ingresar una descripción'),
    endDate: Yup.date().formatdate(),
    link: Yup.string().url('Debes ingresar una URL válida'),
    name: Yup.string().required('Debes ingresar un nombre'),
    position: Yup.number()
      .required('Debes ingresar una posición')
      .typeError('Debes ingresar un número')
      .positive('Debe ser mayor a 0'),
    startDate: Yup.date().formatdate(),
    image: Yup.mixed()
      .nullable()
      .notRequired()
      .test(
        'FILE_SIZE',
        'La imagen cargada excede el tamaño máximo permitido (3 MB).',
        v => !v?.size || v.size <= 3145728
      )
      .test(
        'FILE_FORMAT',
        'El archivo cargado tiene un formato no compatible.',
        v => !v?.type || ['image/jpg', 'image/png', 'image/jpeg', 'image/gif'].includes(v.type)
      ),
    attachedFile: Yup.mixed()
      .nullable()
      .notRequired()
      .test(
        'FILE_SIZE',
        'El archivo cargado excede el tamaño máximo permitido (3 MB).',
        v => !v?.size || v.size <= 3145728
      )
      .test(
        'FILE_FORMAT',
        'El archivo cargado tiene un formato no compatible.',
        v =>
          !v?.type ||
          [
            'image/jpg',
            'image/png',
            'image/jpeg',
            'image/gif',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          ].includes(v.type)
      )
  }),
  rangeDate: Yup.array().rangedate(true)
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(CompanyAnnouncementForm);
